import { COMPONENT_NAME } from '@common/constants/componentName.constant';

/**
 * Converts componentName from various formats (PascalCase, snake_case, kebab-case) to camelCase
 * This function ensures backward compatibility with old metadata that might use different naming conventions
 *
 * @param componentName - The component name in any format
 * @returns The component name in camelCase format
 */
export const normalizeComponentName = (
  componentName: string | undefined | null
): string => {
  if (!componentName) {
    return '';
  }

  // If already in camelCase format (starts with lowercase, no underscores, no hyphens), return as is
  const firstChar = componentName.charAt(0);
  if (
    firstChar === firstChar.toLowerCase() &&
    !componentName.includes('_') &&
    !componentName.includes('-')
  ) {
    return componentName;
  }

  // Handle kebab-case: "horizontal-card-list" -> "horizontalCardList"
  if (componentName.includes('-')) {
    return componentName
      .split('-')
      .filter((word) => word.length > 0)
      .map((word, index) => {
        const normalizedWord = word.toLowerCase();
        return index === 0
          ? normalizedWord
          : normalizedWord.charAt(0).toUpperCase() + normalizedWord.slice(1);
      })
      .join('');
  }

  // Handle snake_case: "horizontal_card_list" -> "horizontalCardList"
  if (componentName.includes('_')) {
    return componentName
      .split('_')
      .filter((word) => word.length > 0)
      .map((word, index) => {
        const normalizedWord = word.toLowerCase();
        return index === 0
          ? normalizedWord
          : normalizedWord.charAt(0).toUpperCase() + normalizedWord.slice(1);
      })
      .join('');
  }

  // Handle PascalCase or mixed case: "HorizontalCardList" -> "horizontalCardList"
  // "Button" -> "button", "SimpleList" -> "simpleList"
  // Convert first character to lowercase and keep the rest as is
  return componentName.charAt(0).toLowerCase() + componentName.slice(1);
};

/**
 * Normalizes componentName and compares it with a constant
 * Useful for backward compatibility when checking componentName in conditions
 *
 * @param componentName - The component name to check (can be in any format)
 * @param constant - The COMPONENT_NAME constant to compare against
 * @returns true if the normalized componentName matches the constant
 */
export const isComponentName = (
  componentName: string | undefined | null,
  constant: string
): boolean => {
  return normalizeComponentName(componentName) === constant;
};

/**
 * Normalizes componentName and checks if it's included in an array of constants
 * Useful for backward compatibility when checking componentName in array conditions
 *
 * @param componentName - The component name to check (can be in any format)
 * @param constants - Array of COMPONENT_NAME constants to check against
 * @returns true if the normalized componentName is included in the constants array
 */
export const isComponentNameIn = (
  componentName: string | undefined | null,
  constants: string[]
): boolean => {
  const normalized = normalizeComponentName(componentName);
  return constants.includes(normalized);
};

/**
 * Normalizes an array of componentNames and checks if any matches the constant
 *
 * @param componentNames - Array of component names to check (can be in any format)
 * @param constant - The COMPONENT_NAME constant to compare against
 * @returns true if any normalized componentName matches the constant
 */
export const includesComponentName = (
  componentNames: (string | undefined | null)[],
  constant: string
): boolean => {
  return componentNames.some(
    (name) => normalizeComponentName(name) === constant
  );
};
